// RecoveryHome.tsx — "Journey". Course-aware: the three pillars completed in
// turn, with the current course expanded into its modules.
import { useState } from 'react';
import { Screen } from '@/components/Screen';
import { Display, Eyebrow, Body, Meta, Pressable, Card, Plate, StatePill } from '@/components/ui';
import { Icon } from '@/components/icons';
import { StepRow } from '@/components/StepRow';
import { RouteToggle } from '@/components/RouteToggle';
import { BackButton } from '@/components/BackButton';
import { useProgress } from '@/hooks/useProgress';
import type { Programme, Step } from '@/types';
import {
  courses,
  currentCourse,
  courseStatus,
  courseProgress,
  modulesOfCourse,
  currentModule,
  moduleStatus,
  moduleName,
  stepStatus,
  isStepOpenable,
} from '@/services/contentProvider';

// (Per-course content tally removed — replaced by the presence line, which counts
//  showing up rather than content consumed.)

export function RecoveryHome({
  programme,
  onOpenModule,
  onOpenStep,
  onBack,
}: {
  programme: Programme;
  onOpenModule: (moduleId: string) => void;
  onOpenStep: (step: Step) => void;
  /** TRIAGE_V2: set when the map was opened from a triage route; returns to it. */
  onBack?: () => void;
}) {
  const progress = useProgress();
  const allCourses = courses(programme);
  const curCourse = currentCourse(programme, progress.state);
  const curMod = currentModule(programme, progress.state);
  const isCompassion = progress.state.activeProgramme === 'compassion';

  // Which act/course the user is browsing on the Journey page. Tapping a pillar
  // switches the view to that act's chapters (the bug was that pillars weren't
  // tappable, so you could only ever see the current course). Defaults to the
  // current course; resets when the track (main/compassion) changes.
  const [viewCourseId, setViewCourseId] = useState<string | null>(null);
  const viewedCourse = (viewCourseId && allCourses.find((c) => c.id === viewCourseId)) || curCourse;
  const isViewingCurrent = !!viewedCourse && !!curCourse && viewedCourse.id === curCourse.id;
  const courseMods = viewedCourse ? modulesOfCourse(programme, viewedCourse) : [];

  return (
    <Screen label="Journey">
      {onBack && <BackButton onClick={onBack} />}
      <RouteToggle />
      {isCompassion ? (
        <>
          <Eyebrow style={{ marginBottom: 9, marginTop: 14, color: 'var(--green-deep)' }}>A softer route</Eyebrow>
          <Display parts={{ pre: 'A gentler ', em: 'self-compassion', post: ' path.' }} size={25} />
          <Body dim size={13.5} style={{ marginTop: 9, maxWidth: 348 }}>
            This is a very light-touch programme. Each day, a small lesson and mostly a single short
            exercise, to gently build the practice of self-compassion over time. It sits alongside your
            main recovery programme as a complement to it, and focuses only on self-compassion, not the
            wider trauma-recovery work. Take it slowly, and return to any week whenever you need.
          </Body>
        </>
      ) : (
        <>
          <Eyebrow style={{ marginBottom: 9, marginTop: 14 }}>Your programme</Eyebrow>
          <Display parts={{ pre: 'Continuing your ', em: 'recovery', post: '.' }} size={25} />
          <Body dim size={13.5} style={{ marginTop: 9, maxWidth: 330 }}>
            Three courses, taken in turn. They open as you go — take as long as you need, and return as
            often as you like.
          </Body>
        </>
      )}

      {/* the three pillars */}
      <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 9 }}>
        {allCourses.map((c, i) => {
          const status = courseStatus(programme, c, progress.state);
          const { done, total } = courseProgress(c, programme, progress.state);
          const isCur = status === 'current';
          const isViewed = viewedCourse?.id === c.id;
          return (
            <Pressable
              key={c.id}
              onClick={() => setViewCourseId(c.id)}
              ariaLabel={`View ${typeof c.title === 'string' ? c.title : 'this course'}`}
            >
            <Card level={isViewed ? 2 : 1} glow={isViewed} pad={14} style={{ opacity: status === 'soon' && !isViewed ? 0.66 : 1, outline: isViewed ? '1.5px solid rgba(200,134,26,0.4)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 999,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      status === 'done'
                        ? 'rgba(29,158,117,0.13)'
                        : isCur
                          ? 'rgba(200,134,26,0.14)'
                          : 'var(--paper)',
                    color: status === 'done' ? 'var(--green-deep)' : 'var(--amber-deep)',
                    fontFamily: 'Fraunces, Georgia, serif',
                    fontSize: 14,
                  }}
                >
                  {status === 'done' ? <Icon name="check" size={17} sw={2.2} /> : i + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Display text={c.title} size={16.5} lh={1.15} style={{ flex: 1, minWidth: 0 }} />
                    <StatePill state={status === 'soon' ? 'soon' : status === 'done' ? 'done' : 'current'} />
                  </div>
                  <Meta style={{ marginTop: 3, display: 'block' }}>
                    {status === 'soon' ? `${total} chapters` : `${done} of ${total} chapters`}
                  </Meta>
                </div>
              </div>
            </Card>
            </Pressable>
          );
        })}
      </div>

      {/* viewed course → its modules (the current act by default; tap a pillar to
          switch). The "You are here" focal only shows for the current act. */}
      {viewedCourse && (
        <div style={{ marginTop: 24 }}>
          <Eyebrow style={{ marginBottom: 11, marginLeft: 2 }}>
            {isViewingCurrent ? 'Current course' : 'Viewing'} · {viewedCourse.title}
          </Eyebrow>

          {/* the rest of this course — connected spine */}
          {courseMods.length > 0 && (
            <div style={{ position: 'relative', marginTop: 22 }}>
              <div style={{ position: 'absolute', left: 13, top: 8, bottom: 16, width: 2, background: 'var(--hair-strong)' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {courseMods.map((m) => {
                  const status = moduleStatus(programme, m, progress.state);
                  const done = status === 'done';
                  const dim = status === 'soon';
                  const isCur = status === 'current';
                  return (
                    <Pressable
                      key={m.id}
                      onClick={() => onOpenModule(m.id)}
                      ariaLabel={isCur ? `Continue chapter ${m.order}` : undefined}
                      style={{
                        display: 'flex',
                        gap: 15,
                        alignItems: 'center',
                        position: 'relative',
                        // Keep left padding at 0 so the disc stays aligned with the spine.
                        padding: isCur ? '11px 12px 11px 0' : '11px 0',
                        opacity: dim ? 0.62 : 1,
                        borderRadius: isCur ? 14 : 0,
                        background: isCur ? 'rgba(200,134,26,0.10)' : 'none',
                        boxShadow: isCur ? 'inset 0 0 0 1.5px rgba(200,134,26,0.34)' : 'none',
                      }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 999,
                          background: 'var(--cream)',
                          border: '2px solid ' + (done ? 'var(--green)' : isCur ? 'var(--amber-deep)' : 'var(--hair-strong)'),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          position: 'relative',
                          zIndex: 1,
                          boxShadow: 'var(--sh1)',
                        }}
                      >
                        {done ? (
                          <span style={{ color: 'var(--green)' }}>
                            <Icon name="check" size={14} sw={2.4} />
                          </span>
                        ) : (
                          <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 12, color: isCur ? 'var(--amber-deep)' : 'var(--ink-soft)' }}>
                            {m.order}
                          </span>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Display text={moduleName(m)} size={15.5} lh={1.15} />
                        <Meta style={{ marginTop: 2, display: 'block' }}>
                          {done ? 'Completed' : isCur ? 'In progress' : `${m.steps.length} steps`}
                        </Meta>
                      </div>
                      {isCur ? (
                        <span
                          style={{
                            flexShrink: 0,
                            fontFamily: 'Inter, system-ui, sans-serif',
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#fff',
                            background: 'var(--amber-deep)',
                            padding: '5px 11px',
                            borderRadius: 999,
                          }}
                        >
                          Continue
                        </span>
                      ) : (
                        <span style={{ color: 'var(--ink-soft)', opacity: 0.4, flexShrink: 0 }}>
                          <Icon name="chevRight" size={15} />
                        </span>
                      )}
                    </Pressable>
                  );
                })}
              </div>
            </div>
          )}

          {/* current module focal — sits below the chapter list (current act only) */}
          {isViewingCurrent && (
          <Card level={2} glow pad={0} style={{ marginTop: courseMods.length > 1 ? 22 : 0 }}>
            <Plate
              ratio="16 / 7"
              radius={0}
              overlay={
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    padding: 15,
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'Inter, system-ui, sans-serif',
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: 'rgba(255,248,236,0.88)',
                      marginBottom: 5,
                      textShadow: '0 1px 6px rgba(0,0,0,0.5)',
                    }}
                  >
                    You are here
                  </span>
                  <Display
                    text={moduleName(curMod)}
                    size={22}
                    color="#fff"
                    style={{ textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}
                  />
                </div>
              }
            />
            <div style={{ padding: '12px 14px 8px' }}>
              {curMod.steps.map((s, i) => (
                <StepRow
                  key={s.id}
                  step={s}
                  status={stepStatus(programme, s, progress.state)}
                  onOpen={() => onOpenStep(s)}
                  last={i === curMod.steps.length - 1}
                  disabled={!isStepOpenable(programme, s, progress.state)}
                  lockedUntilTomorrow={s.id.startsWith('sc-') && !isStepOpenable(programme, s, progress.state) && stepStatus(programme, s, progress.state) === 'current'}
                />
              ))}
            </div>
          </Card>
          )}
        </div>
      )}
    </Screen>
  );
}
